/**
 * PDP gallery: Fancybox Carousel + Thumbs with one Panzoom per image slide.
 * Markup and data come from snippets/fancybox-product-gallery.liquid. On combined-listing
 * changes element-variant-selects.js swaps the wrapper's innerHTML between
 * window.destroyPDPGallery() and window.reinitPDPGallery().
 */

type GalleryConfig = {
  sizes: { thumb: number; small: number; medium: number; large: number };
  imageSizes: string;
  badges: Record<string, { bg: string; text: string }>;
};

type VariantPrice = { id: number; price: number; compare_at_price: number | null };

type VariantUpdatedDetail = {
  variant?: VariantPrice;
  variantId?: number | string;
  variantImageSrc?: string;
  variantImageAlt?: string;
  name?: string;
};

declare global {
  interface Window {
    Carousel?: any;
    Panzoom?: any;
    Thumbs?: any;
    reinitPDPGallery?: () => void;
    destroyPDPGallery?: () => void;
  }
}

const WRAPPER_ID = "pdpGalleryWrapper";
const CAROUSEL_ID = "myCarousel";
const HERO_SELECTOR = `#${CAROUSEL_ID} img#variantImage`;
const HERO_QUALITY = 80;

let carousel: any = null;
let panzooms: any[] = [];
let thumbObserver: MutationObserver | null = null;
// Bumped on destroy so late image loads don't mount Panzoom on a discarded gallery
let generation = 0;
let initScheduled = false;
let heroSwapToken = 0;

function getWrapper(): HTMLElement | null {
  return document.getElementById(WRAPPER_ID);
}

function readJson<T>(selector: string): T | null {
  const text = getWrapper()?.querySelector(selector)?.textContent;
  if (!text) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

function getConfig(): GalleryConfig | null {
  return readJson<GalleryConfig>("[data-gallery-config]");
}

/** Shopify's image_url emits params alphabetically; matching that keeps one cache entry per file. */
function sizedUrl(src: string, width: number, quality?: number): string {
  const url = new URL(src, window.location.href);
  url.searchParams.set("width", String(width));
  if (quality) url.searchParams.set("quality", String(quality));
  url.searchParams.sort();
  return url.href;
}

function pathOf(src: string): string {
  try {
    return new URL(src, window.location.href).pathname;
  } catch {
    return src;
  }
}

/* ---------- Discount badge ---------- */

function updateDiscountBadge(variant: VariantPrice | undefined): void {
  const wrapper = getWrapper()?.querySelector<HTMLElement>("[data-gallery-discount-badge]");
  const value = wrapper?.querySelector<HTMLElement>("[data-gallery-discount-value]");
  const inner = wrapper?.querySelector<HTMLElement>("[data-gallery-discount-badge-inner]");
  const config = getConfig();
  if (!wrapper || !value || !inner || !config) return;

  const compareAt = variant?.compare_at_price ?? 0;
  const price = variant?.price ?? 0;
  const percentage = compareAt > price ? Math.round(((compareAt - price) * 100) / compareAt) : 0;
  if (percentage <= 0) {
    wrapper.classList.add("hidden");
    return;
  }

  const bucket = Math.min(50, Math.max(5, Math.round(percentage / 5) * 5));
  const colors = config.badges[String(bucket)] ?? config.badges["5"];
  wrapper.classList.remove("hidden");
  if (colors) {
    inner.style.backgroundColor = colors.bg;
    inner.style.color = colors.text;
  }
  value.textContent = `${percentage}%`;
}

/* ---------- Images ---------- */

/**
 * Slide images ship with a real src + loading="lazy" (crawlable); flip the active slide and its
 * neighbours to eager so a swipe never lands on an image that hasn't started loading.
 */
function preloadAround(instance: any, index: number): void {
  for (let i = index - 1; i <= index + 1; i++) {
    const slideEl = instance.slides?.[i]?.el as HTMLElement | undefined;
    const img = slideEl?.querySelector<HTMLImageElement>('img[loading="lazy"]');
    if (img) img.loading = "eager";
  }
}

/** Zoom needs the @1600; the hero ships without it (LCP), so add it on first zoom. */
function upgradeForZoom(img: HTMLImageElement, large: number): void {
  if (img.dataset.zoomUpgraded) return;
  img.dataset.zoomUpgraded = "true";
  if (img.id === "variantImage" && !img.srcset.includes(` ${large}w`)) {
    img.srcset = `${img.srcset}, ${sizedUrl(img.currentSrc || img.src, large, HERO_QUALITY)} ${large}w`;
  }
  img.sizes = "200vw";
}

function mountPanzoom(slide: any, gen: number): void {
  if (gen !== generation || slide.panzoom || typeof window.Panzoom !== "function") return;
  const panzoomEl = slide.el?.querySelector(".f-panzoom") as HTMLElement | null;
  const img = panzoomEl?.querySelector("img") as HTMLImageElement | null;
  const config = getConfig();
  if (!panzoomEl || !img || !config) return;

  const { large } = config.sizes;
  if (img.id === "variantImage") {
    // Hero is served ≤@900 for LCP, so naturalWidth ≈ fitWidth and toggleZoom would be a no-op.
    // Tell Panzoom the real full-res size (secondary slides carry data-width/height in markup).
    const width = img.naturalWidth || Number(img.getAttribute("width")) || large;
    const height = img.naturalHeight || Number(img.getAttribute("height")) || large;
    img.dataset.width = String(large);
    img.dataset.height = String(Math.round(height * (large / width)));
  }

  const infoBox = slide.el.querySelector(".galInfoBox") as HTMLElement | null;
  slide.panzoom = new window.Panzoom(panzoomEl, {
    panOnlyZoomed: true,
    bounce: false,
    rubberband: false,
    adaptiveHeight: true,
    wheel: false,
    maxScale: 2,
    on: {
      afterTransform: (instance: any) => {
        if (instance.scale > 1) upgradeForZoom(img, large);
        if (!infoBox) return;
        infoBox.classList.toggle("!hidden", instance.scale > 1 && window.innerWidth <= 796);
      },
    },
  });
  panzooms.push(slide.panzoom);
}

function setupSlide(slide: any): void {
  slide.el.classList.remove("hidden");
  const img = slide.el.querySelector(".f-panzoom img") as HTMLImageElement | null;
  if (!img) return;

  const gen = generation;
  // Hero: Panzoom waits for its own load. Secondary: mount once the lazy image has loaded.
  if (img.id === "variantImage" || (img.complete && img.naturalWidth > 0)) {
    mountPanzoom(slide, gen);
    return;
  }
  img.addEventListener("load", () => mountPanzoom(slide, gen), { once: true });
}

/**
 * Swap the hero to a variant image. Loads off-DOM with the hero's own srcset/sizes so the
 * browser fetches exactly the candidate the hero will use (no extra @1600 request).
 */
async function showVariantImage(src: string, alt: string): Promise<void> {
  const hero = document.querySelector<HTMLImageElement>(HERO_SELECTOR);
  const config = getConfig();
  if (!hero || !config) return;

  const token = ++heroSwapToken;
  if (pathOf(hero.currentSrc || hero.src) === pathOf(src)) {
    carousel?.slideTo(0);
    return;
  }

  const { thumb, small, medium, large } = config.sizes;
  const srcset = `${sizedUrl(src, small, HERO_QUALITY)} ${small}w, ${sizedUrl(src, medium, HERO_QUALITY)} ${medium}w`;
  const fallbackSrc = sizedUrl(src, medium, HERO_QUALITY);

  // load rather than decode(): decode() stays pending while the tab is hidden
  const probe = new Image();
  const loaded = new Promise<boolean>((resolve) => {
    probe.onload = () => resolve(true);
    probe.onerror = () => resolve(false);
  });
  probe.sizes = config.imageSizes;
  probe.srcset = srcset;
  probe.src = fallbackSrc;
  if (!(await loaded)) return;
  if (token !== heroSwapToken || !hero.isConnected) return;

  // Panzoom sizes the hero from data-width/height, so carry the new image's ratio over —
  // otherwise a differently shaped variant renders shrunken.
  const ratio = probe.naturalHeight / probe.naturalWidth;
  if (ratio > 0) {
    hero.setAttribute("height", String(Math.round(medium * ratio)));
    hero.dataset.width = String(large);
    hero.dataset.height = String(Math.round(large * ratio));
  }
  delete hero.dataset.zoomUpgraded;
  hero.sizes = config.imageSizes;
  hero.srcset = srcset;
  hero.src = fallbackSrc;
  hero.alt = alt;

  const thumbSrc = sizedUrl(src, thumb);
  const heroSlide = hero.closest<HTMLElement>(".f-carousel__slide");
  if (heroSlide) {
    heroSlide.dataset.thumbSrc = thumbSrc;
    if (alt) heroSlide.dataset.thumbAlt = alt;
  }
  const firstThumb = getWrapper()?.querySelector<HTMLImageElement>(".f-thumbs__slide__button img");
  if (firstThumb) {
    firstThumb.src = thumbSrc;
    if (alt) firstThumb.alt = alt;
  }

  carousel?.slideTo(0);
}

/* ---------- Videos ---------- */

function syncVideos(instance: any, index: number): void {
  for (const [i, slide] of (instance.slides ?? []).entries()) {
    const video = slide.el?.querySelector("[data-video-element]") as HTMLVideoElement | null;
    if (!video) continue;
    if (i === index) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }
}

/** Fancybox renders thumbs (lazily, per visible range) with alt=""; give them the slide's alt and video badge. */
function decorateThumbs(): void {
  const wrapper = getWrapper();
  if (!wrapper) return;
  const template = wrapper.querySelector<HTMLTemplateElement>("#video-play-icon-template");

  const slides = Array.from(wrapper.querySelectorAll<HTMLElement>(`#${CAROUSEL_ID} .f-carousel__slide`));
  for (const [index, slide] of slides.entries()) {
    const thumbSlide = wrapper.querySelector<HTMLElement>(`.f-thumbs__slide[data-index="${index}"]`);
    if (!thumbSlide) continue;

    const thumbImg = thumbSlide.querySelector<HTMLImageElement>("img");
    const alt = slide.dataset.thumbAlt;
    if (thumbImg && alt && thumbImg.alt !== alt) thumbImg.alt = alt;

    if (!template || slide.dataset.mediaType !== "video" || thumbSlide.querySelector(".video-thumb-play-icon")) continue;
    thumbSlide.classList.add("has-video");
    thumbSlide.appendChild(template.content.cloneNode(true));
  }
}

/** Watch the gallery container itself: the thumbs viewport doesn't exist yet at Carousel `init`. */
function observeThumbs(): void {
  const container = getWrapper()?.querySelector("#productContainer");
  if (!container) return;

  thumbObserver?.disconnect();
  decorateThumbs();
  thumbObserver = new MutationObserver(() => decorateThumbs());
  thumbObserver.observe(container, { childList: true, subtree: true });
}

/* ---------- Progress bars ---------- */

function updateProgress(instance: any): void {
  const count = instance.slides?.length || 1;
  const page = instance.page ?? 0;
  const mobile = document.getElementById("sliderProgressHighlight");
  const desktop = document.getElementById("sliderProgressHighlightDesktop");
  if (mobile) {
    mobile.style.width = `${100 / count}%`;
    mobile.style.left = `${page * (100 / count)}%`;
    mobile.style.transition = "left 0.3s ease";
  }
  if (desktop) {
    desktop.style.height = `${100 / count}%`;
    desktop.style.top = `${page * (100 / count)}%`;
    desktop.style.transition = "top 0.3s ease";
  }
}

/* ---------- Lifecycle ---------- */

function buildOptions(): Record<string, unknown> {
  return {
    transition: "slide",
    Dots: false,
    infinite: false,
    friction: 0.05,
    initialSlide: 0,
    preload: 1,
    Navigation: {
      prevTpl:
        '<div class="bg-white opacity-70 pointer-events-none rounded-full p-2 opacity-70"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" tabindex="-1"><path d="M15 3l-9 9 9 9"/></svg></div>',
      nextTpl:
        '<div class="bg-white opacity-70 pointer-events-none rounded-full p-2 opacity-70"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" tabindex="-1"><path d="M9 3l9 9-9 9"/></svg></div>',
    },
    Panzoom: {
      decelFriction: 0.18,
      dragFriction: 0,
      dragMinThreshold: 50,
      friction: 0.5,
    },
    Thumbs: {
      type: "classic",
      Carousel: {
        axis: "x",
        dragFree: true,
        preload: 3,
        breakpoints: {
          "(min-width: 768px)": { axis: "y" },
        },
        Panzoom: {
          bounce: false,
          rubberband: true,
          decelFriction: 0.12,
          dragFriction: 0.0,
        },
      },
    },
    on: {
      init: () => observeThumbs(),
      // `init` fires before slides exist; `ready` once they're set up
      ready: (instance: any) => {
        preloadAround(instance, instance.page ?? 0);
        updateProgress(instance);
        syncVideos(instance, instance.page ?? 0);
      },
      initSlide: (_instance: any, slide: any) => setupSlide(slide),
      change: (instance: any, to: number, from: number) => {
        instance.slides[from]?.panzoom?.reset(0.15);
        preloadAround(instance, to);
        updateProgress(instance);
        syncVideos(instance, to);
      },
    },
  };
}

export function destroyPDPGallery(): void {
  generation++;
  for (const panzoom of panzooms) panzoom.destroy?.();
  panzooms = [];
  thumbObserver?.disconnect();
  thumbObserver = null;
  carousel?.destroy?.();
  carousel = null;
}

export function initPDPGallery(): void {
  const container = document.getElementById(CAROUSEL_ID);
  if (!container || typeof window.Carousel !== "function") return;

  destroyPDPGallery();
  carousel = new window.Carousel(container, buildOptions(), { Thumbs: window.Thumbs });
}

/**
 * Build the carousel after the LCP paint: the hero is server-rendered and preloaded, secondary
 * slides stay display:none (`hidden`) until initSlide, so this only moves work off the critical path.
 */
function scheduleGalleryInit(): void {
  if (initScheduled || carousel || !document.getElementById(CAROUSEL_ID)) return;
  initScheduled = true;

  const run = () => {
    if (!carousel) initPDPGallery();
  };
  const runWhenIdle = () => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 1);
    }
  };

  if ("PerformanceObserver" in window) {
    try {
      let lcpTimer: number | undefined;
      // requestIdleCallback alone can fire before the final LCP paint on fast machines
      const observer = new PerformanceObserver(() => {
        clearTimeout(lcpTimer);
        lcpTimer = window.setTimeout(runWhenIdle, 50);
      });
      observer.observe({ type: "largest-contentful-paint", buffered: true });
      setTimeout(() => {
        observer.disconnect();
        if (!carousel) runWhenIdle();
      }, 5000);
      return;
    } catch {
      // fall through to load event
    }
  }
  window.addEventListener("load", runWhenIdle, { once: true });
}

function bindInteractionInit(): void {
  // Interacting before the deferred init should build the gallery immediately
  getWrapper()?.addEventListener(
    "pointerdown",
    () => {
      if (!carousel) initPDPGallery();
    },
    { once: true, passive: true },
  );
}

function handleVariantUpdated(event: Event): void {
  const detail = (event as CustomEvent<VariantUpdatedDetail>).detail ?? {};
  const variantId = Number(detail.variant?.id ?? detail.variantId);
  if (!variantId) return;

  const variant = detail.variant ?? readJson<VariantPrice[]>("[data-gallery-variants]")?.find((candidate) => candidate.id === variantId);
  updateDiscountBadge(variant);

  if (detail.variantImageSrc) {
    void showVariantImage(detail.variantImageSrc, detail.variantImageAlt || detail.name || "");
  }
}

function start(): void {
  scheduleGalleryInit();
  bindInteractionInit();
}

window.reinitPDPGallery = initPDPGallery;
window.destroyPDPGallery = destroyPDPGallery;
document.addEventListener("productVariantUpdated", handleVariantUpdated);

// scripts.js runs before the deferred Fancybox scripts; DOMContentLoaded fires after all of them.
if (typeof window.Carousel === "function") {
  start();
} else if (document.readyState !== "complete") {
  document.addEventListener("DOMContentLoaded", start, { once: true });
}

document.addEventListener("shopify:section:load", (event) => {
  if (!(event.target as HTMLElement | null)?.querySelector(`#${CAROUSEL_ID}`)) return;
  initPDPGallery();
});

document.addEventListener("shopify:section:unload", (event) => {
  if (!(event.target as HTMLElement | null)?.querySelector(`#${CAROUSEL_ID}`)) return;
  destroyPDPGallery();
});
